import { CloseOutlined } from '@ant-design/icons';
import { useState } from 'react';

import Button from '@/components/Button/Button';
import IconButton from '@/components/Button/IconButton';
import Input from '@/components/Inputs/Input';
import PasswordInput from '@/components/Inputs/PasswordInput';
import Heading from '@/components/Typography/Heading';
import Typo from '@/components/Typography/Typo';
import { useCreateQualtricsMutation } from '@/entities/(integration)/qualtrics';
import useWorkspaceStore from '@/stores/WorkspaceStore';

type Props = {
  onClose: () => void;
  onSuccess: () => void;
};

const QualtricsTokenModal = ({ onClose, onSuccess }: Props) => {
  const currWorkspace = useWorkspaceStore((state) => state.currWorkspace);

  const workspaceAlias = currWorkspace?.alias;
  const { mutate, isPending } = useCreateQualtricsMutation(
    workspaceAlias ?? '',
  );

  const [apiKey, setApiKey] = useState('');
  const [dataCenterId, setDataCenterId] = useState('');
  const [error, setError] = useState<string | null>(null);

  const isDisabled =
    apiKey.trim() === '' || dataCenterId.trim() === '' || isPending;

  const handleConnect = () => {
    if (isDisabled) return;
    if (!workspaceAlias) return;

    setError(null);
    mutate(
      { api_key: apiKey.trim(), data_center_id: dataCenterId.trim() },
      {
        onSuccess: () => {
          onSuccess();
          onClose();
        },
        onError: () => {
          setError(
            'Failed to connect. Please check your API token and datacenter ID.',
          );
        },
      },
    );
  };

  return (
    <div className="flex flex-col p-8 text-gray-800">
      <div className="flex items-center justify-between">
        <Heading type="h2">Qualtrics Integration</Heading>
        <IconButton
          icon={<CloseOutlined />}
          outlined={false}
          sizeType="l"
          onClick={onClose}
        />
      </div>

      <p className="mt-6 text-sm text-gray-500">
        API token and datacenter ID are required to connect to Qualtrics.
      </p>

      <div className="mt-4">
        <Heading type="h3">API Token</Heading>
        <div className="mt-3">
          <PasswordInput
            sizeType="m"
            placeholder="Paste your Qualtrics API token"
            name="apiKey"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
          />
        </div>
      </div>

      <div className="mt-6">
        <Heading type="h3">Datacenter ID</Heading>
        <div className="mt-3">
          <Input
            sizeType="m"
            placeholder="e.g. syd1"
            value={dataCenterId}
            onChange={(e) => setDataCenterId(e.target.value)}
          />
        </div>
      </div>

      <div className="mt-8">
        {error ? (
          <div className="flex items-center justify-between">
            <Typo type="label2" className="text-red-600">
              {error}
            </Typo>
            <Button
              sizeType="m"
              onClick={handleConnect}
              disabled={isDisabled}
              isLoading={isPending}
            >
              Connect
            </Button>
          </div>
        ) : (
          <div className="flex justify-end">
            <Button
              sizeType="m"
              onClick={handleConnect}
              disabled={isDisabled}
              isLoading={isPending}
            >
              Connect
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default QualtricsTokenModal;
